<template>
  <a
    class="product-card"
    :class="[
      $blockClass(blockClass, 'product-card'),
      {
        [$class('loading')]: !product,
        small,
      }
    ]"
    :href="cleanURL"
  >
    <responsive-image
      :class="$blockClass(blockClass, 'product-card-image')"
      block-class="product-card"
      :image="product.featuredImage"
      min-max="300-2048"
      ratio="4:5"
      :loaded-animation="loadingState"
      :style="{ animationDelay: animationDelay }"
      :lazyload="lazy"
    />

    <responsive-image
      v-if="product?.images?.length > 1"
      class="product-card__secondary-image"
      :image="product.images[1]"
      min-max="300-2048"
      ratio="4:5"
      :loaded-animation="loadingState"
      :style="{ animationDelay: animationDelay }"
    />

    <div
      class="product-card__footer"
      :class="[
        $blockClass(blockClass, 'product-card-footer'),
        expand && 'expand'
      ]"
      :style="{ height: footerHeight }"
    >
      <p
        ref="title"
        class="product-card__title text-body-xs-bold text-body-s-bold-desktop"
        :class="$blockClass(blockClass, 'product-card-title')"
        :style="{ animationDelay: animationDelay }"
        v-text="product.title"
      />

      <div
        v-if="product.subtitle?.value || loadingState"
        class="product-card__subtitle"
        :class="$blockClass(blockClass, 'product-card-subtitle')"
        :style="{ animationDelay: animationDelay }"
      >
        <p
          ref="subtitle"
          class="
            product-card__subtitle-content
            text-body-xs
            text-body-s-desktop
          "
          v-text="formattedSubtitle"
        />
      </div>

      <product-prices
        ref="prices"
        class="text-body-xs-label"
        :class="$blockClass(blockClass, 'product-card-prices')"
        block-class="product-card"
        :compare-at-price="product?.compareAtPrice?.amount"
        :is-gift-card="product?.isGiftCard"
        :price="product?.price?.amount"
        :style="{ animationDelay: animationDelay }"
      />
    </div>
  </a>
</template>

<script>

/**
 * Vue: Product card
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Number} index - Product card's forloop index.
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {Boolean} [loadingState] - Enables the loading state.
 *
 */
import { mapState } from 'vuex'

import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'

export default {
  name: 'ProductCard',

  components: {
    ProductPrices,
    ResponsiveImage,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    index: {
      type: Number,
      default: 0,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    small: {
      type: Boolean,
      default: false,
    },
    loadingState: {
      type: Boolean,
      default: true,
    },
    expand: {
      type: Boolean,
      default: false,
    },
    lazy: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      footerHeight: 89,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute animation delay.
     * @returns {String}
     */
    animationDelay() {
      return this.$timing('quick', { index: this.index, unit: 's' })
    },

    /**
     * Compute clean url to remove search params from PLP.
     * @returns {String}
     */
    cleanURL() {
      if (!this.product) {
        return ''
      }

      const baseUrlPathRegex = /^\/[a-z]{2}-[a-z]{2}\//
      let urlString = this.product.url.replaceAll(window.cnvs.store.shopDomain, '')

      if (!baseUrlPathRegex.test(urlString)) {
        if (urlString.startsWith('/')) {
          urlString = urlString.substring(1)
        }

        const urlObject = new URL(`https:${window.cnvs.store.shopDomain}${urlString}`)

        urlObject.search = ''

        return urlObject.toString()
      }

      const urlObject = new URL(`${window.location.origin}${urlString}`)

      urlObject.search = ''

      return urlObject.toString()
    },

    /**
     * Formats product subtitle.
     * @returns {String}
     */
    formattedSubtitle() {
      if (this.product.subtitle?.value) {
        return this.decodeHtmlEntities(this.product.subtitle.value)
      }
      return ''
    },
  },

  watch: {

    /**
     * Watch is mobile to update footer height.
     */
    isMobile() {
      this.setFooterHeight()
    },

    /**
     * Watch product to update footer height.
     */
    product: {
      handler() {
        if (!this.product) {
          return
        }

        this.setFooterHeight()
      },
      immediate: true,
    },
  },

  methods: {

    /**
     * Set footer height.
     */
    async setFooterHeight() {
      if (!this.expand) {
        return
      }

      if (this.isMobile) {
        this.footerHeight = null
      }

      await this.$nextTick()
      const title = this.$refs.title
      const titleMargin = window.getComputedStyle(title)

      const subtitleHeight = this.$refs.subtitle?.offsetHeight || 0

      const prices = this.$refs.prices.$el

      const footerHeight =
        title.offsetHeight +
        parseInt(titleMargin.marginBottom) +
        subtitleHeight +
        prices.offsetHeight

      this.footerHeight = footerHeight > 89 ? `${footerHeight}px` : '89px'
    },

    /**
     * Decodes Html entities.
     * @returns {String}
     */
    decodeHtmlEntities(text) {
      const textArea = document.createElement('textarea')
      textArea.innerHTML = text
      return textArea.value
    },
  },
}
</script>

<style lang="scss">
@import './product-card';
</style>
